import { H1 } from '@blueprintjs/core'
import { useApi } from '../contexts/ApiProvider'
import { useContext, useState, useEffect, useRef } from 'react'
import { WindowContext } from '../contexts/WindowProvider'
import { useNavigate, useParams } from 'react-router-dom'
import { flattenApiVariables, SurveyContext } from '../contexts/SurveyProvider'
import { validateUploadFile } from '../utils/fileUtils'

// Import sub-components
import UploadInfo from './upload/UploadInfo'
import FileUploadInput from './upload/FileUploadInput'
import UploadProgress from './upload/UploadProgress'

import 'normalize.css'
import '@blueprintjs/core/lib/css/blueprint.css'
import '@blueprintjs/icons/lib/css/blueprint-icons.css'
import './Upload.css'

function Upload() {
  const api = useApi()
  const { surveyId } = useParams()
  const navigate = useNavigate()
  
  // Context hooks
  const { currentSurvey, setSurveys, setCurrentSurvey } = useContext(WindowContext)
  const { setVariables, setPrevVariables } = useContext(SurveyContext)
  
  // Upload states
  const [loading, setLoading] = useState()
  const [error, setError] = useState()
  const [fileInputText, setFileInputText] = useState('Choose a file...')
  
  // Progress tracking states
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadedBytes, setUploadedBytes] = useState(0)
  const [totalBytes, setTotalBytes] = useState(0)
  const [uploadStage, setUploadStage] = useState('idle') // idle, uploading, processing, success, error
  
  // Task polling states
  const [taskId, setTaskId] = useState(null)
  const [taskProgress, setTaskProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const pollingIntervalRef = useRef(null)

  const resetUploadState = () => {
    setUploadProgress(0)
    setUploadedBytes(0)
    setTotalBytes(0)
    setUploadStage('idle')
    setError()
    setTaskId(null)
    setTaskProgress(0)
    setProgressMessage('')
    
    // Clear polling interval if it exists
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }

  const handleUploadSuccess = (survey) => {
    setLoading('success')
    setUploadStage('success')
    
    // Update surveys list
    setSurveys(prev => {
      const oldSurveyIndex = prev.findIndex(s => s.id === Number(surveyId))
      if (oldSurveyIndex !== -1) {
        prev[oldSurveyIndex] = survey
        return prev.sort((a, b) => a.id - b.id)
      }
      return [...prev, survey].sort((a, b) => a.id - b.id)
    })
    
    // Update variables
    const variables = flattenApiVariables(survey.variables)
    setVariables(variables)
    setPrevVariables(variables)
    
    // Update current survey
    setCurrentSurvey(
      Object.fromEntries(
        Object.keys(survey)
          .filter(k => k !== 'variables')
          .map(k => [k, survey[k]])
      )
    )
    
    // Navigate to variables page
    navigate('/survey/' + surveyId + '/variables')
  }

  const handleUploadError = (response) => {
    setFileInputText('Choose a file...')
    setUploadStage('error')
    setLoading('error')
    resetUploadState()
    
    // Set appropriate error message
    if (response.status === 415) {
      setError('Unsupported file format.')
    } else if (response.status === 400) {
      setError('Number of respondents in uploaded data does not match existing survey data.')
    } else {
      setError('An error occurred during upload. Please try again.')
    }
  }

  const uploadFile = async (file) => {
    if (!file) return
    
    // Validate file before upload
    const validation = validateUploadFile(file)
    if (!validation.valid) {
      setError(validation.error)
      return
    }
    
    // Reset state and start upload
    resetUploadState()
    setFileInputText(file.name)
    document.body.style.cursor = 'progress'
    setLoading('loading')
    setTotalBytes(file.size)
    setUploadStage('uploading')

    try {
      const response = await api.put(
        `/survey/${surveyId}/upload`, 
        file,
        {
          onUploadProgress: (percent, loaded, total) => {
            setUploadProgress(percent)
            setUploadedBytes(loaded)
            setTotalBytes(total)
            
            // Switch to processing stage when upload completes
            if (percent === 100) {
              setUploadStage('processing')
            }
          }
        }
      )
      
      if (response.ok) {
        // Response now contains task info, not survey data
        const task = response.body
        setTaskId(task.id)
        setUploadStage('processing')
        startPolling(task.id)
      } else {
        handleUploadError(response)
      }
    } catch (error) {
      console.error('Upload error:', error)
      setError('Network error occurred during upload. Please try again.')
      setUploadStage('error')
      setLoading('error')
      resetUploadState()
    } finally {
      document.body.style.cursor = 'default'
    }
  }

  const cancelUpload = async (taskId) => {
    try {
      const response = await api.post(`/tasks/${taskId}/cancel`)
      if (response.ok) {
        // Clear polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        
        // Reset state
        resetUploadState()
        setError('Upload cancelled')
        setUploadStage('idle')
        setLoading(undefined)
      }
    } catch (error) {
      console.error('Cancel error:', error)
    }
  }
  
  const startPolling = (taskId) => {
    // Poll every second for smooth progress updates
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/task/${taskId}`)
        
        if (response.ok) {
          const task = response.body
          
          // Update progress
          setTaskProgress(task.progress || 0)
          setProgressMessage(task.progress_message || '')
          
          // Check if task is complete
          if (task.complete) {
            clearInterval(interval)
            pollingIntervalRef.current = null
            
            if (task.succeeded) {
              // Fetch the updated survey data
              const surveyResponse = await api.get(`/survey/${surveyId}`)
              if (surveyResponse.ok) {
                handleUploadSuccess(surveyResponse.body)
              } else {
                setError('Failed to fetch survey data after upload')
                setUploadStage('error')
                setLoading('error')
              }
            } else {
              // Task failed
              setError(task.error_message || 'Upload processing failed')
              setUploadStage('error')
              setLoading('error')
            }
          }
        }
      } catch (error) {
        console.error('Polling error:', error)
        // Don't stop polling on error, just log it
      }
    }, 1000) // Poll every second
    
    pollingIntervalRef.current = interval
  }
  
  const handleFileSelect = (file) => {
    resetUploadState()
    uploadFile(file)
  }
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  // Don't render if no current survey
  if (!currentSurvey) {
    return null
  }

  const isUploadInProgress = ['uploading', 'processing'].includes(uploadStage)
  const showUploadForm = ['error', undefined].includes(loading) && uploadStage === 'idle'

  return (
    <div style={{ marginRight: 'auto', marginLeft: 'auto', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          marginTop: '64px',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        {showUploadForm ? (
          <>
            <H1 style={{ marginBottom: '16px' }}>Upload Data File</H1>
            <UploadInfo currentSurvey={currentSurvey} />
            <FileUploadInput
              fileInputText={fileInputText}
              loading={loading}
              onFileSelect={handleFileSelect}
            />
            {error && (
              <p
                style={{
                  color: '#AC2F33',
                  marginTop: '8px',
                  fontWeight: 'bold',
                }}
              >
                {error}
              </p>
            )}
          </>
        ) : isUploadInProgress ? (
          <UploadProgress
            stage={uploadStage}
            fileName={fileInputText}
            progress={uploadStage === 'uploading' ? uploadProgress : taskProgress}
            uploadedBytes={uploadedBytes}
            totalBytes={totalBytes}
            progressMessage={progressMessage}
            onCancel={taskId ? () => cancelUpload(taskId) : null}
          />
        ) : (
          // Fallback UI (shouldn't normally reach here)
          <UploadProgress
            stage="fallback"
            fileName={fileInputText}
            progress={0}
            uploadedBytes={0}
            totalBytes={0}
          />
        )}
      </div>
    </div>
  )
}

export default Upload